import type { ChangeEvent } from 'react';
import { forwardRef } from 'react';

import { useField, useForwardingRef } from '@adflow/frontend/util-common';

import type { InputTextFieldProps } from './InputTextField';
import { InputTextField } from './InputTextField';

export type InputTextFieldWithControlProps = {
	name: string;
	onChangeEffect?: (e: ChangeEvent<HTMLInputElement>) => void;
	context?: Record<string, unknown>;
	autoCompleteOff?: boolean;
	forcedErrorMessage?: string;
} & InputTextFieldProps;

export const InputTextFieldWithControl = forwardRef<
	HTMLInputElement,
	InputTextFieldWithControlProps
>(function InputTextFieldWithControlRender(
	{
		name,
		defaultValue,
		onChangeEffect,
		context,
		forcedErrorMessage,
		min,
		max,
		...other
	},
	forwardedRef
) {
	const {
		field: { ref: fieldRef, onChange, value, ...inputProps },
		fieldState: { invalid },
		errorMessage,
	} = useField({ name, defaultValue, ...context });

	const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
		if (min !== undefined && +e.target.value < +min) {
			e.target.value = min.toString();
		}
		if (max !== undefined && +e.target.value > +max) {
			e.target.value = max.toString();
		}
		onChange(e);
		onChangeEffect?.(e);
	};

	const inputRef = useForwardingRef<HTMLInputElement | null>(null, [
		forwardedRef,
		fieldRef,
	]);

	return (
		<InputTextField
			{...inputProps}
			value={value ?? ''}
			onChange={onChangeHandler}
			inputRef={inputRef}
			error={forcedErrorMessage ? true : invalid}
			helperText={forcedErrorMessage ?? errorMessage}
			{...other}
		/>
	);
});
